import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, Plus, Minus, ArrowUpRight, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { useCart } from "../context/CartContext";
import { createQuote } from "../lib/api";

export default function CheckoutPage() {
  const { items, updateQty, remove, total, clear } = useCart();
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        notes: form.notes || null,
        items: items.map((it) => ({
          id: it.id,
          name: it.name,
          pillar_id: it.pillarId || null,
          pillar_name: it.pillarName || null,
          price: it.price,
          unit: it.unit || null,
          qty: it.qty,
        })),
        total,
      };
      await createQuote(payload);
      setSubmitted(true);
      clear();
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      setError(typeof msg === "string" ? msg : "Please check the form and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section className="pt-[120px] lg:pt-[160px] pb-24 bg-[var(--lc-cream)] min-h-screen">
        <div className="mx-auto max-w-[720px] px-5 lg:px-10 text-center">
          <div className="w-16 h-16 rounded-full bg-[var(--lc-moss)] grid place-items-center mx-auto mb-6">
            <CheckCircle2 className="h-7 w-7 text-white" />
          </div>
          <div className="eyebrow text-[var(--lc-moss)] mb-4">Quote requested</div>
          <h1 className="font-serif-display text-[clamp(36px,5vw,68px)] leading-[1.05] font-light">
            Got it. <span className="italic">Quote on the way.</span>
          </h1>
          <p className="mt-7 text-[15px] leading-relaxed text-[var(--lc-muted)]">
            We'll review your custom package and send your tailored quote to <span className="text-[var(--lc-ink)] font-semibold">{form.email}</span> within 24 hours.
          </p>
          <button
            onClick={() => navigate("/")}
            className="mt-10 inline-flex items-center gap-2 px-6 py-3 bg-[var(--lc-ink)] text-[var(--lc-cream)] text-[12px] font-bold tracking-[0.18em] uppercase hover:bg-[var(--lc-moss-dark)] transition-colors"
          >
            Back to Home <ArrowUpRight className="h-4 w-4" />
          </button>
        </div>
      </section>
    );
  }

  if (items.length === 0) {
    return (
      <section className="pt-[120px] lg:pt-[160px] pb-24 bg-[var(--lc-cream)] min-h-screen">
        <div className="mx-auto max-w-[720px] px-5 lg:px-10 text-center">
          <h1 className="font-serif-display text-[clamp(36px,5vw,64px)] leading-[1.05] font-light">
            Your selection is empty.
          </h1>
          <p className="mt-6 text-[var(--lc-muted)]">
            Pick services across the six pillars to build your custom plan.
          </p>
          <Link
            to="/build"
            className="mt-10 inline-flex items-center gap-2 px-6 py-3 bg-[var(--lc-ink)] text-[var(--lc-cream)] text-[12px] font-bold tracking-[0.18em] uppercase hover:bg-[var(--lc-moss-dark)] transition-colors"
          >
            Build Your Plan <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="pt-[120px] lg:pt-[150px] pb-24 bg-[var(--lc-cream)]">
      <div className="mx-auto max-w-[1400px] px-5 lg:px-10">
        <div className="eyebrow text-[var(--lc-moss)] mb-5">Review & Request</div>
        <h1 className="font-serif-display text-[clamp(38px,5.5vw,72px)] leading-[1] font-light">
          Your custom <span className="italic">package.</span>
        </h1>

        <div className="grid lg:grid-cols-12 gap-10 mt-12">
          {/* Items */}
          <div className="lg:col-span-7">
            <div className="bg-white border border-[var(--lc-line)] rounded-md">
              <div className="px-5 lg:px-7 py-4 border-b border-[var(--lc-line)] flex items-center justify-between">
                <div className="eyebrow text-[var(--lc-muted)]">{items.length} services</div>
                <button
                  onClick={clear}
                  className="text-[11px] font-bold tracking-[0.18em] uppercase text-[var(--lc-muted)] hover:text-[var(--lc-ink)]"
                >
                  Clear all
                </button>
              </div>
              <ul className="divide-y divide-[var(--lc-line)]">
                {items.map((it) => (
                  <li key={it.id} className="px-5 lg:px-7 py-5 flex flex-col sm:flex-row sm:items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--lc-moss)] font-semibold">{it.pillarName}</div>
                      <div className="font-serif-display text-lg leading-tight mt-1">{it.name}</div>
                      <div className="text-xs text-[var(--lc-muted)] mt-1">per {it.unit}</div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="inline-flex items-center border border-[var(--lc-line)] rounded-full">
                        <button onClick={() => updateQty(it.id, it.qty - 1)} className="w-8 h-8 grid place-items-center hover:bg-[var(--lc-cream-soft)] rounded-l-full"><Minus className="h-3 w-3" /></button>
                        <span className="min-w-[28px] text-center text-sm font-semibold">{it.qty}</span>
                        <button onClick={() => updateQty(it.id, it.qty + 1)} className="w-8 h-8 grid place-items-center hover:bg-[var(--lc-cream-soft)] rounded-r-full"><Plus className="h-3 w-3" /></button>
                      </div>
                      <button onClick={() => remove(it.id)} className="p-1.5 text-[var(--lc-muted)] hover:text-[var(--lc-ink)]"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="px-5 lg:px-7 py-5 bg-[var(--lc-cream-soft)]">
                <span className="eyebrow text-[var(--lc-muted)]">Investment</span>
                <div className="font-serif-display text-2xl leading-tight mt-1">
                  Reach out to <span className="italic">discuss.</span>
                </div>
                <div className="text-xs text-[var(--lc-muted)] mt-1">Tailored quote sent within 24 hours</div>
              </div>
            </div>
            <Link
              to="/build"
              className="inline-flex items-center gap-2 mt-5 text-[12px] font-bold tracking-[0.18em] uppercase text-[var(--lc-moss)] hover:gap-3 transition-all"
            >
              + Add more services
            </Link>
          </div>

          {/* Form */}
          <div className="lg:col-span-5">
            <form onSubmit={submit} className="bg-[var(--lc-ink)] text-[var(--lc-cream)] rounded-md p-6 lg:p-8 space-y-5">
              <div className="eyebrow text-[var(--lc-sand)]">Your Details</div>
              <h2 className="font-serif-display text-3xl leading-tight">Request your quote</h2>
              <p className="text-sm text-white/70">We’ll review and send your tailored quote within 24 hours.</p>

              <DarkField label="Full name" required value={form.name} onChange={(v) => update("name", v)} />
              <DarkField label="Email" type="email" required value={form.email} onChange={(v) => update("email", v)} />
              <DarkField label="Phone / WhatsApp" value={form.phone} onChange={(v) => update("phone", v)} />
              <div>
                <label className="eyebrow text-white/60 mb-2 block">Anything we should know? (optional)</label>
                <textarea
                  rows={3}
                  value={form.notes}
                  onChange={(e) => update("notes", e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-sm text-[14px] text-white placeholder-white/40 focus:outline-none focus:border-[var(--lc-sand)] resize-none"
                />
              </div>

              {error && (
                <div className="flex items-start gap-2 px-4 py-3 bg-red-500/15 border border-red-400/40 text-red-200 text-sm rounded-sm">
                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="group flex items-center justify-center gap-2 w-full px-6 py-4 bg-[var(--lc-moss)] hover:bg-[var(--lc-sand)] hover:text-[var(--lc-ink)] text-white text-[12px] font-bold tracking-[0.18em] uppercase transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Sending...
                  </>
                ) : (
                  <>
                    Request Quote
                    <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function DarkField({ label, value, onChange, type = "text", required }) {
  return (
    <div>
      <label className="eyebrow text-white/60 mb-2 block">{label}{required && " *"}</label>
      <input
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-sm text-[14px] text-white placeholder-white/40 focus:outline-none focus:border-[var(--lc-sand)]"
      />
    </div>
  );
}
